type VersionResponse = {
  ok: boolean;
  json: () => Promise<unknown>;
};

type ReleaseRefreshOptions = {
  currentBuildId: string;
  fetch: (input: string, init: { cache: "no-store" }) => Promise<VersionResponse>;
  getVisibilityState: () => DocumentVisibilityState;
  onVisibilityChange: (listener: () => void) => () => void;
  onFocus: (listener: () => void) => () => void;
  getHistoryState: () => unknown;
  replaceHistoryState: (state: unknown) => void;
  reload: () => void;
  getRegistration: () => ServiceWorkerRegistration | undefined;
  getServiceWorkerController: () => ServiceWorker | null;
  onControllerChange: (listener: () => void) => () => void;
  activateWaitingServiceWorker: (reloadPage: false) => Promise<void>;
  hasUnsavedChanges: () => boolean;
  showUpdateAvailable: (activate: () => boolean) => void;
};

export type ReleaseRefreshController = {
  notifyRegistrationReady: () => void;
  notifyUpdateReady: () => void;
  stop: () => void;
};

const RELOAD_GUARD_KEY = "gudwinReloadedBuildId";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readBuildId = (payload: unknown): string | null => {
  if (!isRecord(payload) || typeof payload.buildId !== "string") return null;
  const buildId = payload.buildId.trim();
  return buildId.length > 0 ? buildId : null;
};

export const startReleaseRefresh = (options: ReleaseRefreshOptions): ReleaseRefreshController => {
  const initialController = options.getServiceWorkerController();
  let wasHidden = options.getVisibilityState() === "hidden";
  let versionCheckInFlight: Promise<void> | undefined;
  let registrationUpdateInFlight: Promise<void> | undefined;
  let trailingCheckRequested = false;
  let targetBuildId: string | null = null;
  let updateReady = false;
  let activationStarted = false;
  let updatePromptShown = false;
  let reloadStarted = false;
  let stopped = false;
  let watchedRegistration: ServiceWorkerRegistration | undefined;
  let removeRegistrationUpdateListener: (() => void) | undefined;
  let removeInstallingStateListener: (() => void) | undefined;
  let waitingWorkerTimer: ReturnType<typeof setTimeout> | undefined;

  const reloadWithNewController = (): void => {
    if (stopped || reloadStarted || !targetBuildId) return;
    if (options.hasUnsavedChanges()) {
      showDeferredUpdate();
      return;
    }

    reloadStarted = true;
    const historyState = options.getHistoryState();
    options.replaceHistoryState({
      ...(isRecord(historyState) ? historyState : {}),
      [RELOAD_GUARD_KEY]: targetBuildId,
    });
    options.reload();
  };

  const activateUpdate = (): boolean => {
    if (stopped || activationStarted || !targetBuildId) return false;
    if (options.hasUnsavedChanges()) {
      showDeferredUpdate();
      return false;
    }
    if (options.getServiceWorkerController() !== initialController) {
      reloadWithNewController();
      return true;
    }
    if (!updateReady) return false;

    activationStarted = true;
    void options.activateWaitingServiceWorker(false).catch(() => {
      activationStarted = false;
      updatePromptShown = false;
      showDeferredUpdate();
    });
    return true;
  };

  function showDeferredUpdate(): void {
    if (stopped || updatePromptShown) return;
    updatePromptShown = true;
    options.showUpdateAvailable(activateUpdate);
  }

  const handleUpdateReady = (): void => {
    if (stopped) return;
    // A NetworkFirst navigation can load the new frontend bundle while the old
    // controller is still active. In that state version.json already matches
    // currentBuildId, so the Workbox waiting signal is the only update signal.
    targetBuildId ??= options.currentBuildId;
    updateReady = true;
    if (options.hasUnsavedChanges()) showDeferredUpdate();
    else activateUpdate();
  };

  const watchForWaitingWorker = (registration: ServiceWorkerRegistration): void => {
    if (watchedRegistration === registration) return;
    removeRegistrationUpdateListener?.();
    removeInstallingStateListener?.();
    watchedRegistration = registration;

    const handleUpdateFound = (): void => {
      removeInstallingStateListener?.();
      const installingWorker = registration.installing;
      if (!installingWorker) return;

      const handleStateChange = (): void => {
        if (installingWorker.state !== "installed") return;
        removeInstallingStateListener?.();
        removeInstallingStateListener = undefined;

        const notifyWhenWaiting = (remainingAttempts: number): void => {
          if (stopped) return;
          if (registration.waiting) {
            handleUpdateReady();
            return;
          }
          if (remainingAttempts === 0) return;
          waitingWorkerTimer = setTimeout(() => notifyWhenWaiting(remainingAttempts - 1), 50);
        };
        waitingWorkerTimer = setTimeout(() => notifyWhenWaiting(20), 0);
      };

      installingWorker.addEventListener("statechange", handleStateChange);
      removeInstallingStateListener = () =>
        installingWorker.removeEventListener("statechange", handleStateChange);
      handleStateChange();
    };

    registration.addEventListener("updatefound", handleUpdateFound);
    removeRegistrationUpdateListener = () =>
      registration.removeEventListener("updatefound", handleUpdateFound);
    handleUpdateFound();
  };

  const prepareUpdate = (): void => {
    if (stopped || !targetBuildId || registrationUpdateInFlight) return;
    const registration = options.getRegistration();
    if (!registration) return;
    watchForWaitingWorker(registration);

    registrationUpdateInFlight = registration
      .update()
      .then(() => {
        if (stopped) return;
        if (registration.waiting) handleUpdateReady();
        else if (options.getServiceWorkerController() !== initialController) {
          reloadWithNewController();
        }
      })
      .catch(() => undefined)
      .finally(() => {
        registrationUpdateInFlight = undefined;
      });
  };

  const checkVersion = (): void => {
    if (versionCheckInFlight) {
      trailingCheckRequested = true;
      return;
    }

    versionCheckInFlight = (async () => {
      try {
        const response = await options.fetch("/version.json", { cache: "no-store" });
        if (stopped || !response.ok) return;

        const discoveredBuildId = readBuildId(await response.json());
        if (stopped || !discoveredBuildId || discoveredBuildId === options.currentBuildId) return;

        const historyState = options.getHistoryState();
        if (isRecord(historyState) && historyState[RELOAD_GUARD_KEY] === discoveredBuildId) return;
        if (options.getVisibilityState() !== "visible") return;

        targetBuildId = discoveredBuildId;
        if (options.getServiceWorkerController() !== initialController) {
          reloadWithNewController();
          return;
        }
        prepareUpdate();
      } catch {
        // Version discovery is best-effort: offline and malformed responses keep the current app usable.
      }
    })().finally(() => {
      versionCheckInFlight = undefined;
      if (trailingCheckRequested && !stopped) {
        trailingCheckRequested = false;
        checkVersion();
      }
    });
  };

  const handleVisibilityChange = (): void => {
    const visibilityState = options.getVisibilityState();
    if (visibilityState === "hidden") {
      wasHidden = true;
      return;
    }
    if (visibilityState === "visible" && wasHidden) {
      wasHidden = false;
      checkVersion();
    }
  };

  const removeVisibilityListener = options.onVisibilityChange(handleVisibilityChange);
  const removeFocusListener = options.onFocus(() => {
    if (options.getVisibilityState() === "visible") checkVersion();
  });
  const removeControllerListener = options.onControllerChange(reloadWithNewController);
  checkVersion();

  return {
    notifyRegistrationReady: prepareUpdate,
    notifyUpdateReady: handleUpdateReady,
    stop: () => {
      stopped = true;
      trailingCheckRequested = false;
      removeVisibilityListener();
      removeFocusListener();
      removeControllerListener();
      removeRegistrationUpdateListener?.();
      removeInstallingStateListener?.();
      if (waitingWorkerTimer) clearTimeout(waitingWorkerTimer);
    },
  };
};
